import axios, { AxiosError, AxiosResponse } from "axios";
import { call, put, takeLatest } from "@redux-saga/core/effects";
import storage from "redux-persist/lib/storage";

import { persistReducer } from "redux-persist";
import { RootState } from "data-handler/rootReducer";
import { Category } from "SCConstants";
import {
  listObjectsToListIds,
  listToObject,
} from "data-handler/ducks/paginationTools";
import { errorToast } from "./toast";
import { downloadBlob } from "./utils";

export const STORE_DOCTOR_DATA_REQUEST =
  "schoolconnect/storedoctor/STORE_DOCTOR_DATA_REQUEST";
export const STORE_DOCTOR_DATA_SUCCESS =
  "schoolconnect/storedoctor/STORE_DOCTOR_DATA_SUCCESS";
export const STORE_DOCTOR_DATA_FAILURE =
  "schoolconnect/storedoctor/STORE_DOCTOR_DATA_FAILURE";

export const DETAILED_STOCK_DATA_REQUEST =
  "schoolconnect/storedoctor/DETAILED_STOCK_DATA_REQUEST";
export const DETAILED_STOCK_DATA_SUCCESS =
  "schoolconnect/storedoctor/DETAILED_STOCK_DATA_SUCCESS";
export const DETAILED_STOCK_DATA_FAILURE =
  "schoolconnect/storedoctor/DETAILED_STOCK_DATA_FAILURE";

export const STOCK_MIGRATION_REQUEST =
  "schoolconnect/storedoctor/STOCK_MIGRATION_REQUEST";
export const STOCK_MIGRATION_SUCCESS =
  "schoolconnect/storedoctor/STOCK_MIGRATION_SUCCESS";
export const STOCK_MIGRATION_FAILURE =
  "schoolconnect/storedoctor/STOCK_MIGRATION_FAILURE";

export const STORE_DOCTOR_EXPORT_REQUEST =
  "schoolconnect/storedoctor/STORE_DOCTOR_EXPORT_REQUEST";
export const STORE_DOCTOR_EXPORT_SUCCESS =
  "schoolconnect/storedoctor/STORE_DOCTOR_EXPORT_SUCCESS";
export const STORE_DOCTOR_EXPORT_FAILURE =
  "schoolconnect/storedoctor/STORE_DOCTOR_EXPORT_FAILURE";

export const STORE_DOCTOR_EVENTS_EXPORT_REQUEST =
  "schoolconnect/storedoctor/STORE_DOCTOR_EVENTS_EXPORT_REQUEST";
export const STORE_DOCTOR_EVENTS_EXPORT_SUCCESS =
  "schoolconnect/storedoctor/STORE_DOCTOR_EVENTS_EXPORT_SUCCESS";
export const STORE_DOCTOR_EVENTS_EXPORT_FAILURE =
  "schoolconnect/storedoctor/STORE_DOCTOR_EVENTS_EXPORT_FAILURE";

type StoreDoctor = {
  school_id: number;
  school_Name: string;
  school_state: string;
  country_name: string;
  country_iso3: string;
  stock_id: number;
  commodity_id: number;
  commodity_name: string;
  category: Category;
};

type DetailedStock = {
  id: number;
  event_id: number;
  event_type: string;
  occurred_at: string;
  store_id: number;
  store_name: string;
  stock_name: string;
  type: string;
  target: string;
  sign: number;
  quantity: string;
  balance: string;
  category: Category;
  date_created: string;
  date_updated: string;
  meta: Object;
};

type StoreDoctorData = {
  count: number;
  hidden_inactive?: number;
  next: null | string;
  previous: null | string;
  results: StoreDoctor[];
  ids: number[];
};

type DetailedStockData = {
  count: number;
  next: null | string;
  previous: null | string;
  results: DetailedStock[];
  ids: number[];
};

type State = {
  fetching: boolean;
  stockMigrationFetching: boolean;
  exportFetching: boolean;
  eventsExportFetching: boolean;
  error: any;
  errorResponse: any;
  stockMigrationError: any;
  stockMigrationErrorResponse: any;
  data: StoreDoctorData;
  detailedStock: DetailedStockData;
};

type StoreDoctorDataRequestAction = {
  type: typeof STORE_DOCTOR_DATA_REQUEST;
  pageSize: number;
  pageIndex: number;
  ordering: string;
  filters: any[];
};

type StoreDoctorDataSuccessAction = {
  type: typeof STORE_DOCTOR_DATA_SUCCESS;
  data: StoreDoctorData;
};

type StoreDoctorDataFailureAction = {
  type: typeof STORE_DOCTOR_DATA_FAILURE;
  error: AxiosError;
};

type DetailedStockDataRequestAction = {
  type: typeof DETAILED_STOCK_DATA_REQUEST;
  stockId: number;
  pageSize: number;
  pageIndex: number;
  ordering: string;
  filters: any[];
};

type DetailedStockDataSuccessAction = {
  type: typeof DETAILED_STOCK_DATA_SUCCESS;
  data: DetailedStockData;
};

type DetailedStockDataFailureAction = {
  type: typeof DETAILED_STOCK_DATA_FAILURE;
  error: AxiosError;
};

type StockMigrationRequestAction = {
  type: typeof STOCK_MIGRATION_REQUEST;
  stockId: number;
};

type StockMigrationSuccessAction = {
  type: typeof STOCK_MIGRATION_SUCCESS;
};

type StockMigrationFailureAction = {
  type: typeof STOCK_MIGRATION_FAILURE;
  error: AxiosError;
};

type StoreDoctorExportRequestAction = {
  type: typeof STORE_DOCTOR_EXPORT_REQUEST;
  filters: any[];
};

type StoreDoctorExportSuccessAction = {
  type: typeof STORE_DOCTOR_EXPORT_SUCCESS;
};

type StoreDoctorExportFailureAction = {
  type: typeof STORE_DOCTOR_EXPORT_FAILURE;
  error: AxiosError;
};

type StoreDoctorEventsExportRequestAction = {
  type: typeof STORE_DOCTOR_EVENTS_EXPORT_REQUEST;
  filters: any[];
};

type StoreDoctorEventsExportSuccessAction = {
  type: typeof STORE_DOCTOR_EVENTS_EXPORT_SUCCESS;
};

type StoreDoctorEventsExportFailureAction = {
  type: typeof STORE_DOCTOR_EVENTS_EXPORT_FAILURE;
  error: AxiosError;
};

type Action =
  | StoreDoctorDataRequestAction
  | StoreDoctorDataSuccessAction
  | StoreDoctorDataFailureAction
  | DetailedStockDataRequestAction
  | DetailedStockDataSuccessAction
  | DetailedStockDataFailureAction
  | StockMigrationRequestAction
  | StockMigrationSuccessAction
  | StockMigrationFailureAction
  | StoreDoctorExportRequestAction
  | StoreDoctorExportSuccessAction
  | StoreDoctorExportFailureAction
  | StoreDoctorEventsExportRequestAction
  | StoreDoctorEventsExportSuccessAction
  | StoreDoctorEventsExportFailureAction;

const EMPTY_STORE_DOCTOR_DATA: StoreDoctorData = {
  count: 0,
  hidden_inactive: 0,
  next: null,
  previous: null,
  results: [],
  ids: [],
};

const EMPTY_DETAILED_STOCK_DATA: DetailedStockData = {
  count: 0,
  next: null,
  previous: null,
  results: [],
  ids: [],
};

const initialState: State = {
  fetching: false,
  stockMigrationFetching: false,
  exportFetching: false,
  eventsExportFetching: false,
  error: null,
  errorResponse: null,
  stockMigrationError: null,
  stockMigrationErrorResponse: null,
  data: EMPTY_STORE_DOCTOR_DATA,
  detailedStock: EMPTY_DETAILED_STOCK_DATA,
};

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case STORE_DOCTOR_DATA_REQUEST:
      return {
        ...state,
        fetching: true,
        error: null,
        errorResponse: null,
      };
    case STORE_DOCTOR_DATA_SUCCESS:
      return {
        ...state,
        fetching: false,
        error: null,
        errorResponse: null,
        data: action.data,
      };
    case STORE_DOCTOR_DATA_FAILURE:
      return {
        ...state,
        fetching: false,
        error: action.error,
        errorResponse: action.error.response,
      };
    case DETAILED_STOCK_DATA_REQUEST:
      return {
        ...state,
        fetching: true,
        detailedStock: EMPTY_DETAILED_STOCK_DATA,
        error: null,
        errorResponse: null,
      };
    case DETAILED_STOCK_DATA_SUCCESS:
      return {
        ...state,
        fetching: false,
        error: null,
        errorResponse: null,
        detailedStock: action.data,
      };
    case DETAILED_STOCK_DATA_FAILURE:
      return {
        ...state,
        fetching: false,
        error: action.error,
        errorResponse: action.error.response,
        detailedStock: EMPTY_DETAILED_STOCK_DATA,
      };
    case STOCK_MIGRATION_REQUEST:
      return {
        ...state,
        stockMigrationFetching: true,
        stockMigrationError: null,
        stockMigrationErrorResponse: null,
      };
    case STOCK_MIGRATION_SUCCESS:
      return {
        ...state,
        stockMigrationFetching: false,
        stockMigrationError: null,
        stockMigrationErrorResponse: null,
      };
    case STOCK_MIGRATION_FAILURE:
      return {
        ...state,
        stockMigrationFetching: false,
        stockMigrationError: action.error,
        stockMigrationErrorResponse: action.error.response,
      };
    case STORE_DOCTOR_EXPORT_REQUEST:
      return { ...state, exportFetching: true };
    case STORE_DOCTOR_EXPORT_SUCCESS:
      return { ...state, exportFetching: false };
    case STORE_DOCTOR_EXPORT_FAILURE:
      return { ...state, exportFetching: false };
    case STORE_DOCTOR_EVENTS_EXPORT_REQUEST:
      return { ...state, eventsExportFetching: true };
    case STORE_DOCTOR_EVENTS_EXPORT_SUCCESS:
      return { ...state, eventsExportFetching: false };
    case STORE_DOCTOR_EVENTS_EXPORT_FAILURE:
      return { ...state, eventsExportFetching: false };
    default:
      return state;
  }
};

const persistedReducer = persistReducer(
  {
    key: "storeDoctor",
    storage,
    blacklist: [
      "error",
      "errorResponse",
      "fetching",
      "exportFetching",
      "eventsExportFetching",
    ],
  },
  reducer
);

export default persistedReducer;

function fetchStoreDoctorData(action: StoreDoctorDataRequestAction) {
  const page_size = action.pageSize;
  const page = action.pageIndex * page_size - page_size;
  const filters = action.filters;
  const ordering = action.ordering.replace(/\./g, "_");

  const url =
    `${process.env.REACT_APP_API_URL}/store_manager/store-doctor/` +
    `?limit=${page_size}&offset=${page}` +
    (ordering ? `&ordering=${ordering}` : "") +
    (filters && filters.length
      ? filters
          .map((filter) => {
            const id = filter.id;
            const value = filter.value;
            if (value && value !== "") {
              return `&${id}=${value}`;
            }
            return "";
          })
          .join("")
      : "");

  return axios({
    method: "GET",
    url: url,
  });
}

function fetchDetailedStockData(action: DetailedStockDataRequestAction) {
  const { stockId } = action;
  const page_size = action.pageSize;
  const page = action.pageIndex * page_size - page_size;
  const filters = action.filters;
  const ordering = action.ordering.replace(/\./g, "_");

  const url =
    `${process.env.REACT_APP_API_URL}/store_manager/store-doctor/${stockId}/` +
    `?limit=${page_size}&offset=${page}` +
    (ordering ? `&ordering=${ordering}` : "") +
    (filters && filters.length
      ? filters
          .map((filter) => {
            const id = filter.id;
            const value = filter.value;
            if (value && value !== "") {
              return `&${id}=${value}`;
            }
            return "";
          })
          .join("")
      : "");

  return axios({
    method: "GET",
    url: url,
  });
}

function postStockMigration(stockId: number) {
  return axios({
    method: "POST",
    url: `${process.env.REACT_APP_API_URL}/store_manager/migration-history/`,
    data: { stock_ids: [stockId] },
  });
}

export function* workerStoreDoctorDataSaga(
  action: StoreDoctorDataRequestAction
) {
  try {
    const response: AxiosResponse = yield call(fetchStoreDoctorData, action);
    const results = listToObject(response.data.results, "stock_id");
    const ids = listObjectsToListIds(response.data.results, "stock_id");
    const data = {
      ...response.data,
      results,
      ids,
    };

    yield put({ type: STORE_DOCTOR_DATA_SUCCESS, data });
  } catch (error) {
    yield put({ type: STORE_DOCTOR_DATA_FAILURE, error });
  }
}

export function* workerDetailedStockDataSaga(
  action: DetailedStockDataRequestAction
) {
  try {
    const response: AxiosResponse = yield call(fetchDetailedStockData, action);
    const results = listToObject(response.data.results, "id");
    const ids = listObjectsToListIds(response.data.results, "id");
    const data = {
      ...response.data,
      results,
      ids,
    };

    yield put({ type: DETAILED_STOCK_DATA_SUCCESS, data });
  } catch (error) {
    yield put({ type: DETAILED_STOCK_DATA_FAILURE, error });
  }
}

export function* workerStockMigrationSaga(action: StockMigrationRequestAction) {
  try {
    yield call(postStockMigration, action.stockId);
    yield put({ type: STOCK_MIGRATION_SUCCESS });
    yield call(() => window.location.reload());
  } catch (error) {
    yield put({ type: STOCK_MIGRATION_FAILURE, error });
    yield put(errorToast());
  }
}

export function* storeDoctorDataSagaWatcher() {
  yield takeLatest(STORE_DOCTOR_DATA_REQUEST, workerStoreDoctorDataSaga);
}

export function* detailedStockDataSagaWatcher() {
  yield takeLatest(DETAILED_STOCK_DATA_REQUEST, workerDetailedStockDataSaga);
}

export function* stockMigrationSagaWatcher() {
  yield takeLatest(STOCK_MIGRATION_REQUEST, workerStockMigrationSaga);
}

export const requestStoreDoctorData = (
  pageSize: number,
  pageIndex: number,
  ordering: string,
  filters: any[]
) => ({
  type: STORE_DOCTOR_DATA_REQUEST,
  pageSize,
  pageIndex,
  ordering,
  filters,
});

export const requestDetailedStockData = (
  stockId: number,
  pageSize: number,
  pageIndex: number,
  ordering: string,
  filters: any[]
) => ({
  type: DETAILED_STOCK_DATA_REQUEST,
  stockId,
  pageSize,
  pageIndex,
  ordering,
  filters,
});

export const requestStockMigration = (stockId: number) => ({
  type: STOCK_MIGRATION_REQUEST,
  stockId,
});

export const getStoreDoctorData = (state: RootState): StoreDoctorData =>
  state.storeDoctor.data;

export const getDetailedStockData = (state: RootState): DetailedStockData =>
  state.storeDoctor.detailedStock;

export const getIsStoreDoctorFetching = (state: RootState): boolean =>
  state.storeDoctor.fetching;

export const getStoreDoctorError = (state: RootState): any =>
  state.storeDoctor.error;

export const getStoreDoctorErrorResponse = (state: RootState): any =>
  state.storeDoctor.errorResponse;

export const getIsStockMigrationFetching = (state: RootState): boolean =>
  state.storeDoctor.stockMigrationFetching;

export const getIsStoreDoctorExportFetching = (state: RootState): boolean =>
  state.storeDoctor.exportFetching;

export const requestStoreDoctorExport = (filters: any[]) => ({
  type: STORE_DOCTOR_EXPORT_REQUEST,
  filters,
});

function fetchStoreDoctorExport(action: StoreDoctorExportRequestAction) {
  const filters = action.filters;

  const url =
    `${process.env.REACT_APP_API_URL}/store_manager/store-doctor/export/` +
    `?` +
    (filters && filters.length
      ? filters
          .map((filter) => {
            const id = filter.id;
            const value = filter.value;
            if (value && value !== "") {
              return `${id}=${value}`;
            }
            return "";
          })
          .filter(Boolean)
          .join("&")
      : "");

  return axios({
    method: "GET",
    url: url,
    responseType: "blob",
  });
}

export function* workerStoreDoctorExportSaga(
  action: StoreDoctorExportRequestAction
) {
  try {
    const response: AxiosResponse = yield call(fetchStoreDoctorExport, action);
    const { data: blob } = response;
    const contentDisposition = response.headers["content-disposition"];
    let filename = "store_doctor_export.csv";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];
    }
    downloadBlob(blob, filename);
    yield put({ type: STORE_DOCTOR_EXPORT_SUCCESS });
  } catch (error) {
    yield put({ type: STORE_DOCTOR_EXPORT_FAILURE, error });
    yield put(errorToast());
  }
}

export function* storeDoctorExportSagaWatcher() {
  yield takeLatest(STORE_DOCTOR_EXPORT_REQUEST, workerStoreDoctorExportSaga);
}

// Events export

export const getIsEventsExportFetching = (state: RootState): boolean =>
  state.storeDoctor.eventsExportFetching;

export const requestEventsExport = (filters: any[]) => ({
  type: STORE_DOCTOR_EVENTS_EXPORT_REQUEST,
  filters,
});

function fetchEventsExport(action: StoreDoctorEventsExportRequestAction) {
  const filters = action.filters;

  const url =
    `${process.env.REACT_APP_API_URL}/store_manager/store-doctor/export-events/` +
    `?` +
    (filters && filters.length
      ? filters
          .map((filter) => {
            const id = filter.id;
            const value = filter.value;
            if (value && value !== "") {
              return `${id}=${value}`;
            }
            return "";
          })
          .filter(Boolean)
          .join("&")
      : "");

  return axios({
    method: "GET",
    url: url,
    responseType: "blob",
  });
}

export function* workerEventsExportSaga(
  action: StoreDoctorEventsExportRequestAction
) {
  try {
    const response: AxiosResponse = yield call(fetchEventsExport, action);
    const { data: blob } = response;
    const contentDisposition = response.headers["content-disposition"];
    let filename = "store_doctor_events_export.csv";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];
    }
    downloadBlob(blob, filename);
    yield put({ type: STORE_DOCTOR_EVENTS_EXPORT_SUCCESS });
  } catch (error) {
    yield put({ type: STORE_DOCTOR_EVENTS_EXPORT_FAILURE, error });
    yield put(errorToast());
  }
}

export function* eventsExportSagaWatcher() {
  yield takeLatest(STORE_DOCTOR_EVENTS_EXPORT_REQUEST, workerEventsExportSaga);
}
